import React, { useEffect, useState } from 'react';

import { gmailInviteService, GmailInvitePreview, InviteTarget } from '../services/gmailInviteService';
import { recordingBlur } from '../utils/recordingBlur';

import { InviteEmailCard } from './InviteEmailCard';

interface InviteReviewStepProps {
  batchId: string;
  count: number;
  firstEmail: string | null;
  targetName: string;
  target: InviteTarget;
  dryRun: boolean;
  onBack: () => void;
  onSend: () => Promise<void>;
}

/** The first chosen person's email; with ?blur=1, the sample email, so no one's name shows in a recording. */
const useFirstPreview = (batchId: string, firstEmail: string | null, target: InviteTarget) => {
  const [preview, setPreview] = useState<GmailInvitePreview | null>(null);
  const email = recordingBlur() ? null : firstEmail;

  useEffect(() => {
    let cancelled = false;

    void gmailInviteService.preview(batchId, email, target)
      .then((next) => { if (!cancelled) setPreview(next); })
      .catch(() => undefined);

    return () => { cancelled = true; };
  }, [batchId, email, target]);

  return preview;
};

export const reviewTitle = (count: number, targetName: string, target: InviteTarget): string => {
  const people = `${count} ${count === 1 ? 'person' : 'people'}`;

  if (!target.projectId) return `Invite ${people} to 8080.AI`;

  return `Invite ${people} to ${target.role === 'writer' ? 'edit' : 'view'} ${targetName}`;
};

/** Step 3: what goes out, to whom, and the Send button. */
export const InviteReviewStep: React.FC<InviteReviewStepProps> = (props) => {
  const preview = useFirstPreview(props.batchId, props.firstEmail, props.target);
  const [sending, setSending] = useState(false);
  const invites = `${props.count} ${props.count === 1 ? 'invite' : 'invites'}`;
  const send = () => {
    setSending(true);
    void props.onSend().finally(() => setSending(false));
  };

  return (
    <section className="invite-step">
      <div className="am-header">
        <h3 className="am-title">{reviewTitle(props.count, props.targetName, props.target)}</h3>
        <p className="am-lede">
          Each person gets their own email from your Gmail, with an unsubscribe link.
          {props.target.projectId ? ' They join the project only if they accept.' : ''}
        </p>
      </div>
      <InviteEmailCard preview={preview} />
      <footer className="invite-step__bar">
        <button className="am-secondary" disabled={sending} onClick={props.onBack} type="button">Back</button>
        <button className="am-cta" disabled={sending} onClick={send} type="button">
          {props.dryRun ? `Build ${invites} (test mode)` : `Send ${invites}`}
        </button>
      </footer>
    </section>
  );
};
